/**
 * Type-safe environment variable access with runtime validation
 */

interface EnvConfig {
  // API Configuration
  apiUrl: string;

  // Supabase Configuration
  supabaseUrl: string;
  supabaseAnonKey: string;

  // Authentication
  authCookieName: string;
  authCookieDomain: string;

  // Application Settings
  appName: string;
  appUrl: string;

  // Project Limits
  maxNodesPerProject: number;
  maxEdgesPerProject: number;
  maxProjectsPerUser: number;

  // Cache Settings
  cacheTtl: number;
  cachePrefix: string;
}

function getEnvVar(key: string, required: boolean = true): string {
  const value = import.meta.env[`VITE_${key}`];

  if (required && !value) {
    throw new Error(`Missing required environment variable: VITE_${key}`);
  }

  return value || "";
}

function getEnvNumber(key: string, defaultValue?: number): number {
  const value = getEnvVar(key, defaultValue === undefined);
  if (!value && defaultValue !== undefined) return defaultValue;
  const num = Number(value);
  if (isNaN(num)) {
    throw new Error(`Environment variable VITE_${key} must be a number`);
  }
  return num;
}

export const env: EnvConfig = {
  // API Configuration
  apiUrl: getEnvVar("API_URL", false) || "https://architect-backend.fly.dev",

  // Supabase Configuration
  supabaseUrl: getEnvVar("SUPABASE_URL"),
  supabaseAnonKey: getEnvVar("SUPABASE_ANON_KEY"),

  // Authentication
  authCookieName: getEnvVar("AUTH_COOKIE_NAME"),
  authCookieDomain: getEnvVar("AUTH_COOKIE_DOMAIN"),


  // Application Settings
  appName: getEnvVar("APP_NAME"),
  appUrl: getEnvVar("APP_URL"),
  // supportEmail: getEnvVar("SUPPORT_EMAIL"),

  // Project Limits
  maxNodesPerProject: getEnvNumber("MAX_NODES_PER_PROJECT", 100),
  maxEdgesPerProject: getEnvNumber("MAX_EDGES_PER_PROJECT", 200),
  maxProjectsPerUser: getEnvNumber("MAX_PROJECTS_PER_USER", 10),

  // Cache Settings
  cacheTtl: getEnvNumber("CACHE_TTL", 3600),
  cachePrefix: getEnvVar("CACHE_PREFIX", false) || "metarouter_architect_",
};

// Validate all required environment variables on app startup
Object.entries(env).forEach(([key, value]) => {
  if (value === undefined || value === null) {
    throw new Error(`Missing required environment variable for: ${key}`);
  }
});

export default env;
